import { useState, useEffect, useCallback } from 'react'
import { getDiario, addDiarioEntry, deleteDiarioEntry, updateDiarioEntry } from '@services/crud.js'
import { removerFoto, salvarFotosObra } from '@services/fotos.js'
import type { DiarioClima, DiarioEntry } from '@services/db.js'
import modulosJson from '@content/modulos.json'

// ── Lookup topicoId → módulo + título ────────────────────────────

interface TopicoRef {
  moduloId: string
  moduloTitulo: string
  moduloIcone: string
  topicoTitulo: string
}

type ModuloRaw = {
  id: string
  titulo: string
  icone: string
  topicos: { id: string; titulo: string }[]
}

const TOPICO_LOOKUP: Record<string, TopicoRef> = {}
for (const m of (modulosJson as { modulos: ModuloRaw[] }).modulos) {
  for (const t of m.topicos) {
    TOPICO_LOOKUP[t.id] = {
      moduloId: m.id,
      moduloTitulo: m.titulo,
      moduloIcone: m.icone,
      topicoTitulo: t.titulo,
    }
  }
}

// ── Tipos públicos ────────────────────────────────────────────────

export interface DiarioItemEnriquecido extends Omit<DiarioEntry, 'id'> {
  id: number
  moduloId?: string | undefined
  moduloTitulo?: string | undefined
  moduloIcone?: string | undefined
  topicoTitulo?: string | undefined
}

export interface FiltrosDiario {
  busca: string
  moduloId: string // '' = todos
  mes: string // 'YYYY-MM' ou '' = todos
}

export interface OpcaoTopico {
  value: string
  label: string
}

export interface NovaAnotacao {
  texto: string
  topicoId?: string | undefined
  clima?: DiarioClima | undefined
  equipeIds?: number[] | undefined
  /** Fotos escolhidas no aparelho; são comprimidas e salvas em Arquivos. */
  fotos?: (File | Blob)[] | undefined
}

export interface EdicaoAnotacao {
  texto?: string | undefined
  clima?: DiarioClima | undefined
  equipeIds?: number[] | undefined
  /** Fotos novas a anexar. */
  novasFotos?: (File | Blob)[] | undefined
  /** Ids de fotos a remover (documento é apagado). */
  removerFotoIds?: number[] | undefined
}

function enriquecer(e: DiarioEntry): DiarioItemEnriquecido {
  const ref = e.topicoId ? TOPICO_LOOKUP[e.topicoId] : undefined
  return {
    ...e,
    id: e.id as number,
    moduloId: ref?.moduloId,
    moduloTitulo: ref?.moduloTitulo,
    moduloIcone: ref?.moduloIcone,
    topicoTitulo: ref?.topicoTitulo,
  }
}

function filtrar(items: DiarioItemEnriquecido[], f: FiltrosDiario): DiarioItemEnriquecido[] {
  return items.filter((i) => {
    if (f.moduloId && i.moduloId !== f.moduloId) return false
    if (f.mes && !i.criadoEm.startsWith(f.mes)) return false
    if (f.busca.trim()) {
      const q = f.busca.toLowerCase()
      if (!i.texto.toLowerCase().includes(q) && !i.topicoTitulo?.toLowerCase().includes(q))
        return false
    }
    return true
  })
}

// ── Hook principal ────────────────────────────────────────────────

export function useDiario() {
  const [todos, setTodos] = useState<DiarioItemEnriquecido[]>([])
  const [filtros, setFiltros] = useState<FiltrosDiario>({ busca: '', moduloId: '', mes: '' })
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const raw = await getDiario()
    setTodos(raw.map(enriquecer))
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function registrar(input: NovaAnotacao): Promise<void> {
    const texto = input.texto.trim()
    const temFotos = (input.fotos?.length ?? 0) > 0
    if (!texto && !temFotos) return
    const fotoIds = temFotos
      ? await salvarFotosObra(input.fotos!, {
          topicoId: input.topicoId,
          observacoes: texto ? texto.slice(0, 120) : 'Mídia do diário',
        })
      : []
    const entry: Omit<DiarioEntry, 'id' | 'criadoEm' | 'atualizadoEm'> = {
      texto: texto || (temFotos ? 'Registro de mídia' : ''),
    }
    if (input.topicoId) entry.topicoId = input.topicoId
    if (input.clima) entry.clima = input.clima
    if (input.equipeIds?.length) entry.equipeIds = input.equipeIds
    if (fotoIds.length) entry.fotoIds = fotoIds
    await addDiarioEntry(entry)
    await load()
  }

  async function remover(id: number): Promise<void> {
    await deleteDiarioEntry(id)
    setTodos((prev) => prev.filter((i) => i.id !== id))
  }

  async function editar(id: number, patch: EdicaoAnotacao): Promise<void> {
    const atual = todos.find((i) => i.id === id)
    if (!atual) return
    const texto = patch.texto !== undefined ? patch.texto.trim() : atual.texto
    if (!texto) return

    let fotoIds = [...(atual.fotoIds ?? [])]
    if (patch.removerFotoIds?.length) {
      for (const fid of patch.removerFotoIds) await removerFoto(fid)
      fotoIds = fotoIds.filter((fid) => !patch.removerFotoIds!.includes(fid))
    }
    if (patch.novasFotos?.length) {
      const novos = await salvarFotosObra(patch.novasFotos, {
        topicoId: atual.topicoId,
        observacoes: texto.slice(0, 120),
      })
      fotoIds = [...fotoIds, ...novos]
    }

    const data: Partial<Omit<DiarioEntry, 'id' | 'criadoEm'>> = {
      texto,
      clima: patch.clima !== undefined ? patch.clima : atual.clima,
      equipeIds: patch.equipeIds !== undefined ? patch.equipeIds : atual.equipeIds,
      fotoIds,
    }
    await updateDiarioEntry(id, data)
    await load()
  }

  const itens = filtrar(todos, filtros)

  const mesesDisponiveis: string[] = [...new Set(todos.map((i) => i.criadoEm.slice(0, 7)))].sort(
    (a, b) => b.localeCompare(a),
  )

  return {
    itens,
    todos,
    filtros,
    setFiltros,
    isLoading,
    registrar,
    remover,
    editar,
    mesesDisponiveis,
  }
}

// ── Tópicos por módulo (para o seletor da entrada rápida) ─────────

export function getTopicosPorModulo(moduloId: string): OpcaoTopico[] {
  const mod = (modulosJson as { modulos: ModuloRaw[] }).modulos.find((m) => m.id === moduloId)
  return mod?.topicos.map((t) => ({ value: t.id, label: t.titulo })) ?? []
}
